import { useState, useRef, useEffect, createContext, useContext, useCallback } from "react";
import type { ReportData, SaveStatus, Comment, User } from "@/types";
import MeetingsSection from './MeetingsSection';
import ShowcaseSection from './ShowcaseSection';
import GrowthDashboard from './GrowthDashboard';

export type ReportRole = 'minister' | 'sponsor' | 'mentor';

interface CommentContextType {
  onOpenComments: (prefix?: string) => void;
  sectionComments: Comment[];
}

const CommentContext = createContext<CommentContextType | null>(null);

interface CMRReportProps {
  data: ReportData;
  onChange: (id: string, value: string) => void;
  saveStatus: SaveStatus;
  role: ReportRole;
  status?: string;
  ministerName?: string;
  reportYear?: string;
  onInvite?: (section: 'sponsor' | 'mentor', email: string) => void;
  onLogout?: () => void;
  comments?: Comment[];
  onAddComment?: (sectionId: string, text: string) => Promise<void>;
  isShared?: boolean;
  reportId?: string;
  user?: User;
}

// Which sections each role can edit
const EDITABLE_SECTIONS: Record<ReportRole, string[]> = {
  minister: ['info', 'ministry', 'community', 'professional', 'personal', 'spiritual', 'cocreating', 'relationships', 'showcase', 'meetings'],
  sponsor: ['sponsor', 'showcase', 'meetings'],
  mentor: ['mentor', 'showcase', 'meetings'],
};

const SECTIONS = [
  { id: "welcome", label: "Welcome", icon: "✦" },
  { id: "info", label: "Minister Info", icon: "📋" },
  { id: "ministry", label: "Ministry Details", icon: "🕊" },
  { id: "community", label: "Community", icon: "🤝" },
  { id: "professional", label: "Professional Growth", icon: "🌱" },
  { id: "personal", label: "Personal Development", icon: "💜" },
  { id: "spiritual", label: "Spiritual Development", icon: "🙏" },
  { id: "cocreating", label: "Co-Creating", icon: "🌍" },
  { id: "relationships", label: "Relationships", icon: "🔗" },
  { id: "showcase", label: "Showcase", icon: "✨" },
  { id: "meetings", label: "Meetings", icon: "📅" },
  { id: "growth", label: "Growth", icon: "📊" },
  { id: "sponsor", label: "Sponsor Report", icon: "📝" },
  { id: "mentor", label: "Mentor Report", icon: "📝" },
  { id: "export", label: "Export", icon: "📤" },
];

const watercolorBg = (hue: number, sat = "30%", light = "95%") =>
  `radial-gradient(ellipse at 20% 20%, hsla(${hue},${sat},${light},0.6) 0%, transparent 50%),
   radial-gradient(ellipse at 80% 80%, hsla(${hue + 30},${sat},${light},0.4) 0%, transparent 50%),
   radial-gradient(ellipse at 50% 50%, hsla(${hue + 15},20%,98%,1) 0%, hsla(${hue},10%,99%,1) 100%)`;

const sectionColors: Record<string, number> = {
  welcome: 270, info: 260, ministry: 280, community: 200,
  professional: 140, personal: 300, spiritual: 250,
  cocreating: 170, relationships: 210, showcase: 310, meetings: 195,
  growth: 145, sponsor: 35, mentor: 35, export: 220,
};

const TIMEZONE_OPTIONS = [
  { group: 'US / Canada', zones: [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'America/Phoenix', label: 'Arizona (no DST)' },
    { value: 'America/Toronto', label: 'Toronto (ET)' },
    { value: 'America/Vancouver', label: 'Vancouver (PT)' },
  ]},
  { group: 'Europe', zones: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  ]},
  { group: 'Asia-Pacific', zones: [
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
  ]},
];

const Field = ({ label, id, data, onChange, type = "text", hint, wordLimit, readOnly, options }: {
  label: string; id: string; data: ReportData; onChange: (id: string, val: string) => void;
  type?: string; hint?: string; wordLimit?: string; readOnly?: boolean; options?: string[];
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const commentCtx = useContext(CommentContext);
  const [aiFeedback, setAiFeedback] = useState<{ isComplete: boolean; feedback: string } | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  
  useEffect(() => {
    if (type === "textarea" && textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [data[id], type]);

  const val = data[id] || "";
  const words = val.trim() ? val.trim().split(/\s+/).length : 0;
  
  // Parse limit (handles "25" or "25-30")
  const limit = wordLimit ? parseInt(wordLimit.split('-').pop() || "0") : 0;
  const isOver = limit > 0 && words > limit;

  const checkCompleteness = async () => {
    if (!val || val.length < 10) return;
    setIsChecking(true);
    setAiFeedback(null);
    try {
      const res = await fetch('/api/ai/check-completeness', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fieldLabel: label, value: val }),
      });
      if (res.ok) {
        const result = await res.json();
        setAiFeedback(result);
      }
    } catch (err) {
      console.error('AI check failed:', err);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div style={{ marginBottom: 20, opacity: readOnly ? 0.7 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <label style={{ display: "block", fontWeight: 600, color: "#4a3660", fontSize: 14, marginBottom: 6 }}>
          {label}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {!readOnly && type === "textarea" && (
            <button 
              onClick={checkCompleteness}
              disabled={isChecking || val.length < 10}
              style={{ 
                background: 'rgba(124,77,186,0.05)', border: '1px solid rgba(124,77,186,0.2)', 
                cursor: (isChecking || val.length < 10) ? 'default' : 'pointer', padding: '2px 8px', borderRadius: '8px',
                fontSize: 12, color: '#7c4dba', transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: 4, opacity: val.length < 10 ? 0.5 : 1
              }}
              title="Check if your answer fully addresses the question"
            >
              {isChecking ? '⏳' : '✨'} <span style={{ fontWeight: 600 }}>{isChecking ? 'Checking...' : 'Check Completeness'}</span>
            </button>
          )}
          {commentCtx && (
            <button 
              onClick={() => commentCtx.onOpenComments(`${label}: `)}
              style={{ 
                background: 'rgba(124,77,186,0.05)', border: '1px solid rgba(124,77,186,0.2)', 
                cursor: 'pointer', padding: '2px 8px', borderRadius: '8px',
                fontSize: 12, color: '#7c4dba', transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: 4
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(124,77,186,0.1)'; e.currentTarget.style.borderColor = '#7c4dba'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(124,77,186,0.05)'; e.currentTarget.style.borderColor = 'rgba(124,77,186,0.2)'; }}
              title="Comment on this specific field"
            >
              🗨️ <span style={{ fontWeight: 600 }}>Comment</span>
            </button>
          )}
        </div>
      </div>
      {hint && <p style={{ margin: "0 0 6px", fontSize: 12, color: "#8a7a9a", fontStyle: "italic" }}>{hint}</p>}
      {type === "textarea" ? (
        <textarea
          ref={textAreaRef}
          value={val}
          onChange={e => !readOnly && onChange(id, e.target.value)}
          readOnly={readOnly}
          rows={5}
          style={{
            width: "100%", padding: 14, borderRadius: 12, border: "1.5px solid #d4c5e8",
            fontFamily: "'Georgia', serif", fontSize: 15, lineHeight: 1.7, resize: readOnly ? "none" : "vertical",
            background: readOnly ? "rgba(240,237,245,0.7)" : "rgba(255,255,255,0.7)", boxSizing: "border-box",
            transition: "border-color 0.2s", cursor: readOnly ? "default" : undefined,
            overflow: "hidden",
            borderColor: isOver ? "#dc2626" : "#d4c5e8"
          }}
          onFocus={e => !readOnly && (e.target.style.borderColor = isOver ? "#dc2626" : "#9b72cf")}
          onBlur={e => e.target.style.borderColor = isOver ? "#dc2626" : "#d4c5e8"}
        />
      ) : type === "select" && options ? (
      <select
        value={val}
        onChange={e => !readOnly && onChange(id, e.target.value)}
        disabled={readOnly}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #d4c5e8",
          fontFamily: "'Georgia', serif", fontSize: 15,
          background: readOnly ? "rgba(240,237,245,0.7)" : "rgba(255,255,255,0.7)",
          boxSizing: "border-box", transition: "border-color 0.2s", cursor: readOnly ? "default" : "pointer",
          appearance: "auto",
        }}
        onFocus={e => !readOnly && (e.target.style.borderColor = "#9b72cf")}
        onBlur={e => e.target.style.borderColor = "#d4c5e8"}
      >
        <option value="">Select...</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    ) : (
      <input
        type={type}
        value={val}
        onChange={e => !readOnly && onChange(id, e.target.value)}
        readOnly={readOnly}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #d4c5e8",
          fontFamily: "'Georgia', serif", fontSize: 15,
          background: readOnly ? "rgba(240,237,245,0.7)" : "rgba(255,255,255,0.7)",
          boxSizing: "border-box", transition: "border-color 0.2s", cursor: readOnly ? "default" : undefined,
        }}
        onFocus={e => !readOnly && (e.target.style.borderColor = "#9b72cf")}
        onBlur={e => e.target.style.borderColor = "#d4c5e8"}
      />
    )}
    
    {aiFeedback && (
      <div style={{ 
        marginTop: 8, padding: '10px 14px', borderRadius: 12, 
        background: aiFeedback.isComplete ? 'rgba(5,150,105,0.05)' : 'rgba(220,38,38,0.05)',
        border: `1px solid ${aiFeedback.isComplete ? 'rgba(5,150,105,0.2)' : 'rgba(220,38,38,0.2)'}`,
        fontSize: 13, color: aiFeedback.isComplete ? '#059669' : '#dc2626',
        display: 'flex', gap: 8, alignItems: 'center'
      }}>
        <span>{aiFeedback.isComplete ? '✅' : '💡'}</span>
        <span style={{ flex: 1 }}>{aiFeedback.feedback}</span>
        <button 
          onClick={() => setAiFeedback(null)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, opacity: 0.5, padding: 4 }}
        >✕</button>
      </div>
    )}

    {wordLimit && (
      <div style={{ 
        textAlign: "right", fontSize: 11, marginTop: 4, 
        color: isOver ? "#dc2626" : "#8a7a9a", 
        fontWeight: isOver ? 700 : 400 
      }}>
        {words} / {wordLimit} words {isOver && " (over limit)"}
      </div>
    )}
  </div>
  );
};

const Radio = ({ label, id, options, data, onChange, readOnly }: {
  label: string; id: string; options: string[]; data: ReportData;
  onChange: (id: string, val: string) => void; readOnly?: boolean;
}) => {
  const commentCtx = useContext(CommentContext);
  return (
    <div style={{ marginBottom: 20, opacity: readOnly ? 0.7 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <label style={{ display: "block", fontWeight: 600, color: "#4a3660", fontSize: 14, marginBottom: 8 }}>{label}</label>
        {commentCtx && (
          <button 
            onClick={() => commentCtx.onOpenComments(`${label}: `)}
            style={{ 
              background: 'rgba(124,77,186,0.05)', border: '1px solid rgba(124,77,186,0.2)', 
              cursor: 'pointer', padding: '2px 8px', borderRadius: '8px',
              fontSize: 12, color: '#7c4dba', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 4
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(124,77,186,0.1)'; e.currentTarget.style.borderColor = '#7c4dba'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(124,77,186,0.05)'; e.currentTarget.style.borderColor = 'rgba(124,77,186,0.2)'; }}
            title="Comment on this specific field"
          >
            🗨️ <span style={{ fontWeight: 600 }}>Comment</span>
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {options.map(opt => (
          <button key={opt} onClick={() => !readOnly && onChange(id, opt)} style={{
            padding: "8px 20px", borderRadius: 20,
            border: data[id] === opt ? "2px solid #7c4dba" : "1.5px solid #d4c5e8",
            background: data[id] === opt ? "linear-gradient(135deg, #7c4dba, #9b72cf)" : "rgba(255,255,255,0.6)",
            color: data[id] === opt ? "#fff" : "#5a4a6a", fontWeight: 500,
            cursor: readOnly ? "default" : "pointer", transition: "all 0.2s", fontSize: 14
          }}>{opt}</button>
        ))}
      </div>
    </div>
  );
};

const SectionCard = ({ title, subtitle, children, hue, sectionId, comments, onAddComment }: {
  title?: string; subtitle?: string; children: React.ReactNode; hue: number;
  sectionId?: string; comments?: Comment[]; onAddComment?: (sectionId: string, text: string) => Promise<void>;
}) => {
  const [showComments, setShowComments] = useState(false);
  const [initialComment, setInitialComment] = useState<string | undefined>();
  const commentPanelRef = useRef<HTMLDivElement>(null);
  const sectionComments = comments?.filter(c => c.sectionId === sectionId) || [];

  const handleOpenComments = (prefix?: string) => {
    setShowComments(true);
    if (prefix) setInitialComment(prefix);
  };

  useEffect(() => {
    if (showComments && commentPanelRef.current) {
      commentPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showComments]);

  return (
    <CommentContext.Provider value={{ onOpenComments: handleOpenComments, sectionComments }}>
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <div style={{
          background: watercolorBg(hue), borderRadius: 20, padding: "32px 28px",
          boxShadow: "0 2px 20px rgba(100,60,140,0.06)",
          border: "1px solid rgba(180,160,210,0.2)",
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {title && <h3 style={{ margin: "0 0 4px", color: "#3a2a50", fontSize: 20, fontFamily: "'Georgia', serif" }}>{title}</h3>}
              {subtitle && <p style={{ margin: "0 0 20px", color: "#7a6a8a", fontSize: 13, fontStyle: "italic" }}>{subtitle}</p>}
            </div>
            {sectionId && sectionComments.length > 0 && (
              <button
                onClick={() => setShowComments(!showComments)}
                style={{
                  background: 'rgba(124,77,186,0.1)',
                  border: '1.5px solid #7c4dba',
                  borderRadius: '12px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#7c4dba',
                  boxShadow: '0 2px 8px rgba(124,77,186,0.15)'
                }}
              >
                <span style={{ fontSize: 16 }}>💬</span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{sectionComments.length} {sectionComments.length === 1 ? 'Comment' : 'Comments'}</span>
              </button>
            )}
          </div>
          {children}
        </div>

        {showComments && sectionId && (
          <div ref={commentPanelRef} style={{
            marginTop: 12,
            background: '#fff',
            borderRadius: 16,
            padding: 20,
            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
            border: '1px solid #eee',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h4 style={{ margin: 0, fontSize: 14, color: '#4a3660' }}>Comments</h4>
              <button onClick={() => { setShowComments(false); setInitialComment(undefined); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#8a7a9a' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
              {sectionComments.length === 0 ? (
                <p style={{ fontSize: 13, color: '#8a7a9a', fontStyle: 'italic', textAlign: 'center' }}>No comments yet. Start the conversation!</p>
              ) : (
                sectionComments.map(c => (
                  <div key={c.commentId} style={{ 
                    background: '#fcfaff', 
                    padding: '10px 14px', 
                    borderRadius: 12,
                    borderLeft: `3px solid ${c.role === 'minister' ? '#9b72cf' : c.role === 'sponsor' ? '#d4a853' : '#4a9c7f'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#5a4a6a' }}>
                        {c.userName} <span style={{ fontWeight: 400, color: '#8a7a9a', fontSize: 10 }}>({c.role})</span>
                      </span>
                      <span style={{ fontSize: 10, color: '#b0a0c0' }}>
                        {(() => {
                          const d = new Date(c.createdAt);
                          return isNaN(d.getTime()) ? 'Just now' : `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </span>                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#3a2a50', lineHeight: 1.5 }}>{c.text}</p>
                  </div>
                ))
              )}
            </div>

            <CommentInput 
              initialValue={initialComment} 
              onAdd={async text => { 
                if (onAddComment) await onAddComment(sectionId, text);
                setInitialComment(undefined);
              }} 
            />
          </div>
        )}
      </div>
    </CommentContext.Provider>
  );
};

const CommentInput = ({ onAdd, initialValue }: { onAdd: (text: string) => Promise<void>; initialValue?: string }) => {
  const [text, setText] = useState(initialValue || '');
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialValue) {
      setText(initialValue);
      inputRef.current?.focus();
    }
  }, [initialValue]);

  const handleSubmit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onAdd(text);
      setText('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        ref={inputRef}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        placeholder="Type a comment..."
        style={{
          flex: 1, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #d4c5e8',
          fontSize: 13, outline: 'none'
        }}
      />
      <button 
        onClick={handleSubmit} 
        disabled={!text.trim() || sending}
        style={{
          background: '#7c4dba', color: '#fff', border: 'none', borderRadius: 10,
          padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          opacity: text.trim() && !sending ? 1 : 0.5
        }}
      >
        Post
      </button>
    </div>
  );
};

const generatePlainText = (data: ReportData, reportYear: string) => {
  let t = `${reportYear} NEW COMPREHENSIVE MINISTER'S REPORT\n`;
  t += "================================================\n\n";

  t += "I. MINISTER INFORMATION\n";
  t += `Name: ${data.name || ''}\n`;
  t += `Reporting Year: ${data.reportingYear || ''}\n`;
  t += `Date Licensed: ${data.dateFirstLicensed || ''}\n`;
  t += `Letter of Call Date: ${data.letterOfCallDate || ''}\n`;
  t += `Position: ${data.positionTitle || ''}\n`;
  t += `Ministry Type: ${data.typeOfMinistry || ''}\n`;
  t += `Ministry Name: ${data.communityName || ''}\n`;
  t += `Ministry Address: ${data.communityAddress || ''}\n`;
  t += `Personal Address: ${data.personalAddress || ''}\n`;
  t += `Email: ${data.email || ''}\n`;
  t += `Phone: ${data.phone || ''}\n`;
  t += `Sponsor: ${data.sponsorName || ''}\n`;
  t += `Mentor: ${data.mentorNameCommunity || ''}\n\n`;

  t += "II. MINISTRY DETAILS\n";
  t += `Position Type: ${data.positionType || ''}\n`;
  t += `Salary: ${data.monthlySalary || ''}\n`;
  t += `Other Income: ${data.otherIncome || ''}\n`;
  t += `Classes: ${data.classesIncome || ''}\n`;
  t += `Workshops: ${data.workshopsIncome || ''}\n`;
  t += `Staff Role: ${data.staffRole || ''}\n`;
  t += `Monthly Tithe: ${data.monthlyTithe || ''}\n`;
  t += `Hours/Week: ${data.avgHoursWeek || ''}\n\n`;

  t += "III. SERVICE & ACTIVITIES\n";
  t += `Duties: ${data.duties || ''}\n`;
  t += `People Served/Week: ${data.peopleServedWeek || ''}\n`;
  t += `Personal Support/Week: ${data.personalSupport || ''}\n`;
  t += `Classes Taught: ${data.classesWorkshopsList || ''}\n`;
  t += `Ceremonies: ${data.ceremoniesList || ''}\n`;
  t += `Other Activities: ${data.otherActivities || ''}\n\n`;

  t += "IV. COMMUNITY REFLECTION\n";
  t += `a) Describe Community: ${data.communityDescribe || ''}\n`;
  t += `b) Financial Wellness: ${data.communityFinancial || ''}\n`;
  t += `c) CSL Support: ${data.communitySupportCSL || ''}\n`;
  t += `d) Embodied Minister: ${data.communityEmbodied || ''}\n`;
  t += `e) Role of Community: ${data.communityRole || ''}\n\n`;

  t += "V. PROFESSIONAL DEVELOPMENT\n";
  t += `Successes: ${data.profSuccess || ''}\n`;
  t += `Improvements: ${data.profImprove || ''}\n`;
  t += `Action Steps: ${data.profActionSteps || ''}\n`;
  t += `Conferences: ${data.profConferences || ''}\n\n`;

  t += "VI. PERSONAL DEVELOPMENT\n";
  t += `Relationship Style: ${data.personalRelStyle || ''}\n`;
  t += `Difficult Relationships: ${data.personalDifficult || ''}\n`;
  t += `Growth Challenges: ${data.personalChallenges || ''}\n`;
  t += `Disappointment: ${data.personalDisappointment || ''}\n`;
  t += `Joy/Sadness: ${data.personalJoySadness || ''}\n`;
  t += `Self-Care: ${data.personalSelfCare || ''}\n`;
  t += `Balance: ${data.personalBalance || ''}\n`;
  t += `Network: ${data.personalNetwork || ''}\n`;
  t += `CSL Participation: ${data.personalCSLInfo || ''}\n\n`;

  t += "VII. SPIRITUALITY\n";
  t += `Divine Understanding: ${data.spiritDivine || ''}\n`;
  t += `Connection: ${data.spiritConnection || ''}\n`;
  t += `Practices: ${data.spiritPractices || ''}\n`;
  t += `Alignment: ${data.spiritAlignment || ''}\n\n`;

  t += "VIII. SPIRITUAL DEVELOPMENT\n";
  t += `a) Maturity: ${data.spiritMaturity || ''}\n`;
  t += `b) Past Challenges: ${data.spiritPastChallenges || ''}\n`;
  t += `c) Current Challenges: ${data.spiritCurrentChallenges || ''}\n`;
  t += `d) Go-To: ${data.spiritGoTo || ''}\n`;
  t += `e) Childhood: ${data.spiritChildhood || ''}\n`;
  t += `f) Holding Back: ${data.spiritHoldBack || ''}\n`;
  t += `g) Transformation: ${data.spiritTransformation || ''}\n\n`;

  t += "IX. CO-CREATING\n";
  t += `Biases: ${data.cocreateBiases || ''}\n`;
  t += `DEIJB: ${data.cocreateDEIJB || ''}\n`;
  t += `Discomfort: ${data.cocreateDiscomfort || ''}\n`;
  t += `Healing: ${data.cocreateHealing || ''}\n`;
  t += `Role: ${data.cocreateRole || ''}\n`;
  t += `Summary: ${data.cocreateSummary || ''}\n\n`;

  t += "X. RELATIONSHIPS\n";
  t += `Sponsor Relationship: ${data.relSponsor || ''}\n`;
  t += `Mentor Relationship: ${data.relMentor || ''}\n\n`;

  t += "XI. SPONSOR REPORT\n";
  t += `Sponsor: ${data.spRepName || ''} (${data.spRepCommunity || ''})\n`;
  t += `Relationship: ${data.spRepRelationship || ''}\n`;
  t += `Enhance: ${data.spRepEnhance || ''}\n`;
  t += `Job Expectations: ${data.spRepJobDesc || ''}\n`;
  t += `Spiritual Progress: ${data.spRepSpiritual || ''}\n`;
  t += `Dev Plan: ${data.spRepDevPlan || ''}\n`;
  t += `Ethical Issues: ${data.spRepEthical || ''}\n`;
  t += `Improvement: ${data.spRepImprovement || ''}\n`;
  t += `Feedback: ${data.spRepFeedback || ''}\n`;
  t += `Signature: ${data.spRepSignature || ''} (${data.spRepDate || ''})\n\n`;

  t += "XII. MENTOR REPORT\n";
  t += `Mentor: ${data.mnRepName || ''} (${data.mnRepCommunity || ''})\n`;
  t += `Relationship: ${data.mnRepRelationship || ''}\n`;
  t += `Enhance: ${data.mnRepEnhance || ''}\n`;
  t += `Dev Plan: ${data.mnRepDevPlan || ''}\n`;
  t += `Healthy Ministry: ${data.mnRepHealthy || ''}\n`;
  t += `Improvement: ${data.mnRepImprovement || ''}\n`;
  t += `Feedback: ${data.mnRepFeedback || ''}\n`;
  t += `Signature: ${data.mnRepSignature || ''} (${data.mnRepDate || ''})\n`;

  return t;
};

// Reusable sub-components for "copy from previous year"
const CopyBanner = ({ prevYear, title, description, onCopy, onOverwrite }: {
  prevYear: string; title: string; description: string;
  onCopy: () => void; onOverwrite: () => void;
}) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    padding: '12px 16px', borderRadius: 14, marginBottom: 20,
    background: 'linear-gradient(135deg, rgba(124,77,186,0.06), rgba(155,114,207,0.10))',
    border: '1px solid rgba(124,77,186,0.15)',
  }}>
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: 13, color: '#4a3660', fontWeight: 600, marginBottom: 2 }}>
        📋 {title}
      </div>
      <div style={{ fontSize: 12, color: '#8a7a9a' }}>
        {description}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <button
        onClick={onCopy}
        style={{
          padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #7c4dba, #9b72cf)', color: '#fff',
          fontSize: 13, fontWeight: 600, transition: 'transform 0.2s',
          boxShadow: '0 3px 10px rgba(124,77,186,0.25)',
        }}
        onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.03)')}
        onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
        title="Only fills in empty fields — won't overwrite anything you've already entered"
      >
        Copy from {prevYear}
      </button>
      <button
        onClick={onOverwrite}
        style={{
          padding: '8px 14px', borderRadius: 20,
          border: '1.5px solid rgba(124,77,186,0.2)',
          background: 'rgba(255,255,255,0.6)', color: '#7c4dba',
          fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
        }}
        title="Overwrites all fields with the previous year's data"
      >
        Overwrite all
      </button>
    </div>
  </div>
);

const CopyConfirmation = ({ prevYear, message }: { prevYear: string; message?: string }) => (
  <div style={{
    padding: '10px 16px', borderRadius: 12, marginBottom: 20,
    background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.15)',
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, color: '#059669', fontWeight: 500,
  }}>
    ✅ {message || `Info copied from ${prevYear}! Review and update as needed.`}
  </div>
);

export default function CMRReport({ data, onChange, saveStatus, role, status = 'draft', ministerName, reportYear = '2026', onInvite, onLogout, comments, onAddComment, isShared, reportId, user }: CMRReportProps) {
  const [section, setSection] = useState("welcome");
  const [showNav, setShowNav] = useState(false);
  const [inviteEmail, setInviteEmail] = useState({ sponsor: '', mentor: '' });
  const contentRef = useRef<HTMLDivElement>(null);

  // Previous year report (for copy-from feature)
  const [prevYearData, setPrevYearData] = useState<Record<string, string> | null>(null);
  const [prevYear, setPrevYear] = useState<string | null>(null);
  const [prevYearLoading, setPrevYearLoading] = useState(false);
  const [copiedSections, setCopiedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (role !== 'minister' || !reportId) return;
    setPrevYearLoading(true);
    fetch('/api/reports/mine', { credentials: 'include' })
      .then(res => res.ok ? res.json() : { reports: [] })
      .then(({ reports }) => {
        const currentYearNum = parseInt(reportYear);
        const prevReports = (reports || [])
          .filter((r: any) => r.role === 'minister' && r.reportId !== reportId && parseInt(r.reportYear) < currentYearNum)
          .sort((a: any, b: any) => parseInt(b.reportYear) - parseInt(a.reportYear));
        if (prevReports.length > 0) {
          setPrevYear(prevReports[0].reportYear);
          setPrevYearData(prevReports[0].data || {});
        }
      })
      .catch(() => {})
      .finally(() => setPrevYearLoading(false));
  }, [role, reportId, reportYear]);

  // Fields that can be copied per section
  const COPYABLE_SECTION_FIELDS: Record<string, string[]> = {
    info: [
      'name', 'dateFirstLicensed', 'letterOfCallDate', 'positionTitle',
      'typeOfMinistry', 'communityName', 'communityAddress', 'personalAddress',
      'email', 'phone', 'sponsorName', 'mentorNameCommunity',
      'sponsorEmail', 'mentorEmail', 'sponsorTimezone', 'mentorTimezone',
    ],
    ministry: [
      'positionType', 'monthlySalary', 'otherIncome', 'classesIncome',
      'workshopsIncome', 'staffRole', 'monthlyTithe', 'avgHoursWeek',
      'duties', 'peopleServedWeek', 'personalSupport',
      'classesWorkshopsList', 'ceremoniesList', 'otherActivities',
    ],
    community: [
      'communityDescribe', 'communityFinancial', 'communitySupportCSL',
      'communityEmbodied', 'communityRole',
    ],
  };

  const handleCopySection = useCallback((sectionKey: string, overwrite: boolean) => {
    if (!prevYearData) return;
    const fields = COPYABLE_SECTION_FIELDS[sectionKey] || [];
    for (const field of fields) {
      if (prevYearData[field] && (overwrite || !data[field])) {
        onChange(field, prevYearData[field]);
      }
    }
    setCopiedSections(prev => new Set(prev).add(sectionKey));
    setTimeout(() => setCopiedSections(prev => {
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    }), 4000);
  }, [prevYearData, data, onChange]);

  const ro = !EDITABLE_SECTIONS[role].includes(section);
  const totalFields = 60;
  const filledFields = Object.values(data).filter(v => v && v.length > 0).length;
  const pct = Math.min(100, Math.round((filledFields / totalFields) * 100));

  const goTo = (id: string) => {
    setSection(id);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const curIdx = SECTIONS.findIndex(s => s.id === section);
  const next = () => { if (curIdx < SECTIONS.length - 1) goTo(SECTIONS[curIdx + 1].id); };
  const prev = () => { if (curIdx > 0) goTo(SECTIONS[curIdx - 1].id); };

  const exportPlain = () => {
    const text = generatePlainText(data, reportYear);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CMR_Report_${data.name || 'Export'}_${reportYear}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveStatusText = saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'All changes saved' : saveStatus === 'error' ? 'Error saving' : '';
  const saveStatusColor = saveStatus === 'error' ? '#dc2626' : '#9a80c0';

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  return (
    <div style={{
      display: "flex", height: "100vh", background: "#f8f5fc", color: "#2d1b4e",
      fontFamily: "'Inter', sans-serif", overflow: "hidden",
    }}>
      {/* Mobile Header with Hamburger */}
      {isMobile && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, height: 60,
          background: "#2d1b4e", color: "#fff", display: "flex",
          alignItems: "center", padding: "0 16px", zIndex: 90,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}>
          <button
            onClick={() => setShowNav(true)}
            style={{
              background: "none", border: "none", color: "#fff",
              fontSize: 24, cursor: "pointer", padding: 8, display: "flex"
            }}
          >
            ☰
          </button>
          <div style={{ marginLeft: 12, fontSize: 14, fontWeight: 700, fontFamily: "'Georgia', serif" }}>
            Minister's Report
          </div>
        </div>
      )}

      {/* Sidebar navigation */}
      <nav style={{
        width: isMobile ? "280px" : "280px",
        position: isMobile ? "fixed" : "relative",
        zIndex: 100, top: 0, left: 0, height: "100%",
        background: "#2d1b4e", color: "#fff", transition: "transform 0.3s ease",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        ...(isMobile ? {
          transform: showNav ? "translateX(0)" : "translateX(-100%)",
          boxShadow: showNav ? "0 0 30px rgba(0,0,0,0.3)" : "none"
        } : {}),
      }}>
        <div style={{ padding: "0 20px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: "#b8a0d8" }}>Centers for Spiritual Living</div>
            {isMobile && (
              <button
                onClick={() => setShowNav(false)}
                style={{
                  background: "none", border: "none", color: "#a090b8",
                  fontSize: 20, cursor: "pointer", padding: 0
                }}
              >
                ✕
              </button>
            )}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Georgia', serif" }}>Minister's Report</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, color: "#9a80c0" }}>{reportYear} Annual Review</div>
            <span style={{
              fontSize: 9, padding: "2px 8px", borderRadius: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1,
              background: role === 'minister' ? "rgba(155,114,207,0.3)" : role === 'sponsor' ? "rgba(200,160,60,0.3)" : "rgba(100,180,160,0.3)",
              color: role === 'minister' ? "#d4bfff" : role === 'sponsor' ? "#e0c080" : "#80d0b0",
            }}>{role}</span>
            <span style={{
              fontSize: 9, padding: "2px 8px", borderRadius: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1,
              background: status === 'draft' ? "rgba(200,180,100,0.25)" : "rgba(100,200,140,0.25)",
              color: status === 'draft' ? "#c8b060" : "#70c090",
            }}>{status === 'draft' ? 'Draft' : status}</span>
          </div>
        </div>
        <div style={{ padding: "16px 20px 8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: "#9a80c0" }}>Progress: {pct}%</div>
            {saveStatusText && (
              <div style={{ fontSize: 10, color: saveStatusColor, fontWeight: 500 }}>
                {saveStatusText}
              </div>
            )}
          </div>
          <div style={{ height: 4, background: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
            <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #9b72cf, #c8a2f0)", borderRadius: 2, transition: "width 0.4s ease" }} />
          </div>
        </div>
        <div style={{ flex: 1, padding: "8px 10px", overflowY: "auto" }}>
          {SECTIONS.map((s) => {
            const hasComments = (comments || []).some(c => c.sectionId === s.id);
            return (
              <button key={s.id} onClick={() => { goTo(s.id); if (isMobile) setShowNav(false); }} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "10px 12px", border: "none", borderRadius: 10,
                cursor: "pointer",
                background: section === s.id ? "rgba(155,114,207,0.25)" : "transparent",
                color: section === s.id ? "#d4bfff" : "#a090b8",
                fontSize: 13, fontWeight: section === s.id ? 600 : 400,
                textAlign: "left", transition: "all 0.2s", marginBottom: 2,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{s.icon}</span>
                  {s.label}
                </div>
                {hasComments && (
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%', background: '#9b72cf',
                    boxShadow: '0 0 6px rgba(155,114,207,0.5)', marginRight: 4
                  }} />
                )}
              </button>
            );
          })}
        </div>
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          {onLogout && (
            <button onClick={onLogout} style={{
              width: "100%", padding: "8px 12px", marginBottom: 12, borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
              color: "#a090b8", fontSize: 12, cursor: "pointer", transition: "all 0.2s",
            }}>Sign Out</button>
          )}
          <div style={{ fontSize: 10, color: "#7a6a90", lineHeight: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              Where Love Shapes Community<br />
              And Community Shapes Us
            </div>
            <div style={{ opacity: 0.5 }}>v1.0.5</div>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {isMobile && showNav && (
        <div
          onClick={() => setShowNav(false)}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 95, backdropFilter: "blur(2px)"
          }}
        />
      )}

      {/* Main content */}
      <main ref={contentRef} style={{ flex: 1, overflowY: "auto", padding: isMobile ? "80px 16px 100px" : "40px 48px 100px" }}>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>

          {section === "welcome" && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{
                width: 120, height: 120, borderRadius: "50%", margin: "0 auto 24px",
                background: watercolorBg(270, "40%", "88%"),
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span style={{ fontSize: 48 }}>✨</span>
              </div>
              <h1 style={{ fontFamily: "'Georgia', serif", color: "#2d1b4e", fontSize: 32, margin: "0 0 16px" }}>
                Welcome, {ministerName || "Minister"}
              </h1>
              <p style={{ color: "#7a6a8a", fontSize: 16, maxWidth: 540, margin: "0 auto 32px", lineHeight: 1.8 }}>
                It's time for your {reportYear} Annual Comprehensive Minister's Report. This digital workspace 
                is designed for reflection, growth, and collaboration with your Sponsor and Mentor.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, maxWidth: 600, margin: "0 auto" }}>
                <div style={{ padding: 24, borderRadius: 20, background: "rgba(124,77,186,0.05)", border: "1.5px solid rgba(124,77,186,0.1)", textAlign: "left" }}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>📝</div>
                  <h3 style={{ fontSize: 14, margin: "0 0 8px", color: "#4a3660" }}>Auto-Save</h3>
                  <p style={{ fontSize: 12, margin: 0, color: "#7a6a8a", lineHeight: 1.6 }}>Your progress is saved automatically to Firestore as you type.</p>
                </div>
                <div style={{ padding: 24, borderRadius: 20, background: "rgba(124,77,186,0.05)", border: "1.5px solid rgba(124,77,186,0.1)", textAlign: "left" }}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>💬</div>
                  <h3 style={{ fontSize: 14, margin: "0 0 8px", color: "#4a3660" }}>Collaboration</h3>
                  <p style={{ fontSize: 12, margin: 0, color: "#7a6a8a", lineHeight: 1.6 }}>Use the 💬 icons on any section to discuss details with your Sponsor and Mentor.</p>
                </div>
              </div>
              <button onClick={next} style={{
                marginTop: 40, padding: "14px 40px", borderRadius: 30, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #7c4dba, #9b72cf)", color: "#fff",
                fontSize: 16, fontWeight: 600, boxShadow: "0 4px 15px rgba(124,77,186,0.3)",
                transition: "transform 0.2s",
              }} onMouseOver={e => (e.target as HTMLButtonElement).style.transform = "scale(1.03)"}
                 onMouseOut={e => (e.target as HTMLButtonElement).style.transform = "scale(1)"}>
                Begin My Report
              </button>
            </div>
          )}

          {section === "info" && (
            <>
              {ro && <div style={{ background: "rgba(100,60,180,0.08)", borderRadius: 12, padding: "12px 16px", marginBottom: 16, border: "1px solid rgba(150,100,200,0.15)" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#6a5a7a", fontStyle: "italic" }}>You are viewing this section as {role}. Only the minister can edit these fields.</p>
              </div>}
              <SectionCard title="Minister Information" subtitle={(!prevYearData || role !== 'minister') ? "Let's start with the basics." : undefined} hue={260} sectionId="info" comments={comments} onAddComment={onAddComment}>
                {role === 'minister' && prevYearData && prevYear && !copiedSections.has('info') && (
                  <CopyBanner
                    prevYear={prevYear}
                    title="Previous report found"
                    description={`Pre-fill your info from your ${prevYear} report — you can still edit everything after.`}
                    onCopy={() => handleCopySection('info', false)}
                    onOverwrite={() => handleCopySection('info', true)}
                  />
                )}
                {copiedSections.has('info') && (
                  <CopyConfirmation prevYear={prevYear!} />
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="Name" id="name" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Reporting Year (Jan-Dec)" id="reportingYear" data={data} onChange={onChange} hint={`e.g., Jan-Dec ${reportYear}`} readOnly={ro} />
                  <Field label="Date First Licensed" id="dateFirstLicensed" data={data} onChange={onChange} type="date" readOnly={ro} />
                  <Field label="Letter of Call Date" id="letterOfCallDate" data={data} onChange={onChange} type="date" readOnly={ro} />
                </div>
                <Field label="Position Title" id="positionTitle" data={data} onChange={onChange} type="select" options={["Senior Minister", "Assistant Minister", "Associate Minister", "Staff Minister"]} readOnly={ro} />
                <Field label="Type of Ministry" id="typeOfMinistry" data={data} onChange={onChange} type="select" options={["Community", "Teaching Community/Chapter"]} readOnly={ro} />
                <Field label="Community/Ministry Name" id="communityName" data={data} onChange={onChange} readOnly={ro} />
                <Field label="Community/Ministry Address" id="communityAddress" data={data} onChange={onChange} readOnly={ro} />
                <Field label="Personal Address" id="personalAddress" data={data} onChange={onChange} readOnly={ro} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="E-mail" id="email" data={data} onChange={onChange} type="email" readOnly={ro} />
                  <Field label="Phone" id="phone" data={data} onChange={onChange} type="tel" readOnly={ro} />
                </div>
                <Field label="Senior Minister/Sponsor Name (Community if different)" id="sponsorName" data={data} onChange={onChange} readOnly={ro} />
                <Field label="Mentor Name & Community" id="mentorNameCommunity" data={data} onChange={onChange} readOnly={ro} />
              </SectionCard>
              {role === 'minister' && (
                <SectionCard title="Invite Sponsor & Mentor" subtitle="Enter their email addresses and timezones so they can sign in and complete their sections." hue={270} sectionId="info" comments={comments} onAddComment={onAddComment}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0 12px", alignItems: "end" }}>
                    <Field label="Sponsor Email" id="sponsorEmail" data={data} onChange={onChange} type="email" hint="Your sponsor will be able to view the full report and edit the Sponsor section." readOnly={ro} />
                    <div style={{ marginBottom: 20 }}>
                      <button onClick={() => { if (data.sponsorEmail && onInvite) onInvite('sponsor', data.sponsorEmail); }} disabled={!data.sponsorEmail} style={{
                        padding: "10px 20px", borderRadius: 10, border: "none", cursor: data.sponsorEmail ? "pointer" : "default",
                        background: data.sponsorEmail ? "linear-gradient(135deg, #7c4dba, #9b72cf)" : "#d4c5e8",
                        color: "#fff", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                      }}>Send Invite</button>
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#3a2a50', marginBottom: 6 }}>Sponsor Timezone</label>
                    <select
                      value={data.sponsorTimezone || ''}
                      onChange={e => onChange('sponsorTimezone', e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: '1.5px solid rgba(180,160,210,0.3)', background: 'rgba(255,255,255,0.7)',
                        fontSize: 14, color: '#3a2a50', cursor: 'pointer', outline: 'none',
                      }}
                    >
                      <option value="">Select timezone...</option>
                      {TIMEZONE_OPTIONS.map(g => (
                        <optgroup key={g.group} label={g.group}>
                          {g.zones.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9a8ab0', fontStyle: 'italic' }}>Used when scheduling meetings to show times in their timezone.</p>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0 12px", alignItems: "end" }}>
                    <Field label="Mentor Email" id="mentorEmail" data={data} onChange={onChange} type="email" hint="Your mentor will be able to view the full report and edit the Mentor section." readOnly={ro} />
                    <div style={{ marginBottom: 20 }}>
                      <button onClick={() => { if (data.mentorEmail && onInvite) onInvite('mentor', data.mentorEmail); }} disabled={!data.mentorEmail} style={{
                        padding: "10px 20px", borderRadius: 10, border: "none", cursor: data.mentorEmail ? "pointer" : "default",
                        background: data.mentorEmail ? "linear-gradient(135deg, #7c4dba, #9b72cf)" : "#d4c5e8",
                        color: "#fff", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                      }}>Send Invite</button>
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#3a2a50', marginBottom: 6 }}>Mentor Timezone</label>
                    <select
                      value={data.mentorTimezone || ''}
                      onChange={e => onChange('mentorTimezone', e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: '1.5px solid rgba(180,160,210,0.3)', background: 'rgba(255,255,255,0.7)',
                        fontSize: 14, color: '#3a2a50', cursor: 'pointer', outline: 'none',
                      }}
                    >
                      <option value="">Select timezone...</option>
                      {TIMEZONE_OPTIONS.map(g => (
                        <optgroup key={g.group} label={g.group}>
                          {g.zones.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9a8ab0', fontStyle: 'italic' }}>Used when scheduling meetings to show times in their timezone.</p>
                  </div>
                </SectionCard>
              )}
            </>
          )}

          {section === "ministry" && (
            <>
              <SectionCard title="Ministry Information" subtitle={(!prevYearData || role !== 'minister') ? "Tell us about your role and service." : undefined} hue={280} sectionId="ministry" comments={comments} onAddComment={onAddComment}>
                {role === 'minister' && prevYearData && prevYear && !copiedSections.has('ministry') && (
                  <CopyBanner
                    prevYear={prevYear}
                    title="Use last year's details as a starting point"
                    description={`Your ${prevYear} ministry details can save you time — copy them over, then update anything that's changed this year.`}
                    onCopy={() => handleCopySection('ministry', false)}
                    onOverwrite={() => handleCopySection('ministry', true)}
                  />
                )}
                {copiedSections.has('ministry') && (
                  <CopyConfirmation prevYear={prevYear!} message="Ministry details copied — please review and update for this year's changes." />
                )}
                <Radio label="This position is:" id="positionType" options={["A paid position", "A volunteer position"]} data={data} onChange={onChange} readOnly={ro} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="Monthly Salary" id="monthlySalary" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Other Ministerial Income (monthly avg)" id="otherIncome" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Classes" id="classesIncome" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Workshops" id="workshopsIncome" data={data} onChange={onChange} readOnly={ro} />
                </div>
                <Field label="Specific Staff Minister Role" id="staffRole" data={data} onChange={onChange} hint="e.g., Pastoral Care, Youth & Family, Education Coordinator" readOnly={ro} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="Personal monthly tithe to your community" id="monthlyTithe" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Average hours worked per week" id="avgHoursWeek" data={data} onChange={onChange} readOnly={ro} />
                </div>
              </SectionCard>
              <SectionCard title="Service & Activities" subtitle="The scope of your ministry." hue={290} sectionId="ministry" comments={comments} onAddComment={onAddComment}>
                <Field label="Duties/Responsibilities (from LOC or Job Description)" id="duties" data={data} onChange={onChange} type="textarea" hint="You may also reference a separate attached document." readOnly={ro} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="People your ministry serves in an average week" id="peopleServedWeek" data={data} onChange={onChange} hint="Total: online, in person, services, classes, workshops, individual sessions" readOnly={ro} />
                  <Field label="People you personally provide ministerial support to weekly" id="personalSupport" data={data} onChange={onChange} readOnly={ro} />
                </div>
                <Field label="Classes/workshops taught in the past 12 months" id="classesWorkshopsList" data={data} onChange={onChange} type="textarea" readOnly={ro} />
                <Field label="Ceremonies performed/participated in" id="ceremoniesList" data={data} onChange={onChange} type="textarea" hint="Weddings, Memorials, Ordinations during reporting year" readOnly={ro} />
                <Field label="Other activities/services offered or provided" id="otherActivities" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              </SectionCard>
            </>
          )}

          {section === "community" && (
            <SectionCard title="Community" subtitle={(!prevYearData || role !== 'minister') ? "Reflect on your spiritual community and your place within it." : undefined} hue={200} sectionId="community" comments={comments} onAddComment={onAddComment}>
              {role === 'minister' && prevYearData && prevYear && !copiedSections.has('community') && (
                <CopyBanner
                  prevYear={prevYear}
                  title="Start with your previous reflections"
                  description={`Your ${prevYear} community reflections can be a helpful starting point. Copy them over, then update with this year's growth and changes.`}
                  onCopy={() => handleCopySection('community', false)}
                  onOverwrite={() => handleCopySection('community', true)}
                />
              )}
              {copiedSections.has('community') && (
                <CopyConfirmation prevYear={prevYear!} message="Community reflections copied — take a moment to update them with this year's perspective." />
              )}
              <Field label="a) Describe your spiritual community" id="communityDescribe" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="b) Describe the financial wellness of your community" id="communityFinancial" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="c) Indicate your community's financial support of CSL" id="communitySupportCSL" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="d) How have you embodied being a minister in your role?" id="communityEmbodied" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="e) What role does spiritual community play in your spiritual life?" id="communityRole" data={data} onChange={onChange} type="textarea" readOnly={ro} />
            </SectionCard>
          )}

          {section === "professional" && (
            <SectionCard title="Professional Development" subtitle="Consider your service as a minister during the past year." hue={140} sectionId="professional" comments={comments} onAddComment={onAddComment}>
              <Field label="2 most significant examples of success in your ministry" id="profSuccess" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="3 most significant things to improve, change, learn, or develop" id="profImprove" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Action steps you and your Sponsor have created to achieve those goals" id="profActionSteps" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Conferences or conventions attended this year" id="profConferences" data={data} onChange={onChange} type="textarea" hint="CSL, ANTN, IANTA, World Parliament of Religions, Conference of the Americas, etc. (in-person or virtual)" readOnly={ro} />
            </SectionCard>
          )}

          {section === "personal" && (
            <SectionCard title="Personal Development & Self Care" subtitle="How you're growing, caring for yourself, and connecting." hue={300} sectionId="personal" comments={comments} onAddComment={onAddComment}>
              <Field label="Describe your interpersonal relationship style" id="personalRelStyle" data={data} onChange={onChange} type="textarea" hint="How do you interact with your Senior Minister, Practitioners, Board, and other leaders?" readOnly={ro} />
              <Field label="How do you react to and resolve difficult relationships?" id="personalDifficult" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="2 examples of challenges that resulted in growth" id="personalChallenges" data={data} onChange={onChange} type="textarea" hint="For each: What was the challenge? How did you handle it? What did you learn? What was the outcome?" readOnly={ro} />
              <Field label="How have you handled disappointment in ministry?" id="personalDisappointment" data={data} onChange={onChange} type="textarea" hint="What was the disappointment, how did you handle it, what did you learn, what was the outcome?" readOnly={ro} />
              <Field label="How are you experiencing both joy and sadness? (25-30 words)" id="personalJoySadness" data={data} onChange={onChange} type="textarea" wordLimit="25-30" readOnly={ro} />
              <Field label="2 examples of daily self-care practices" id="personalSelfCare" data={data} onChange={onChange} type="textarea" hint="How do they impact your daily activities and life?" readOnly={ro} />
              <Field label="How do you balance work and leisure?" id="personalBalance" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="2 examples of expanding your ministerial network" id="personalNetwork" data={data} onChange={onChange} type="textarea" hint="What are you doing and how frequently?" readOnly={ro} />
              <Field label="CSL informational opportunities participation" id="personalCSLInfo" data={data} onChange={onChange} type="textarea" hint="Connection Call, Tune In Tuesday, E-newsletter, etc." readOnly={ro} />
            </SectionCard>
          )}

          {section === "spiritual" && (
            <>
              <div style={{ background: `linear-gradient(135deg, rgba(100,60,180,0.08), rgba(180,120,255,0.05))`, borderRadius: 16, padding: "16px 20px", marginBottom: 20, border: "1px solid rgba(150,100,200,0.15)" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#6a5a7a", fontStyle: "italic" }}>
                  ✦ Self-Assessment must not exceed 10 pages, including questions.
                </p>
              </div>
              <SectionCard title="Spirituality" subtitle="Ministry is directed by our theology. Describe your foundational understanding." hue={250} sectionId="spiritual" comments={comments} onAddComment={onAddComment}>
                <Field label="Describe your understanding of the Divine (25 words or less)" id="spiritDivine" data={data} onChange={onChange} type="textarea" wordLimit="25" hint="What is the Divine? Personal or impersonal? Immanent or transcendent? Conscious or unconscious?" readOnly={ro} />
                <Field label="How do you experience your personal connection with the Divine?" id="spiritConnection" data={data} onChange={onChange} type="textarea" hint="How does your connection manifest?" readOnly={ro} />
                <Field label="Describe your spiritual practices and their effects" id="spiritPractices" data={data} onChange={onChange} type="textarea" hint="Address this in mind, body, and soul." readOnly={ro} />
                <Field label="How can you tell when you are out of alignment?" id="spiritAlignment" data={data} onChange={onChange} type="textarea" hint="Body, mind, spirit, or all three? When that occurs, what do you do?" readOnly={ro} />
              </SectionCard>
              <SectionCard title="Spiritual Development" subtitle="We are always in a process of continuous spiritual development." hue={240} sectionId="spiritual" comments={comments} onAddComment={onAddComment}>
                <Field label="a) Spiritually immature vs. spiritually mature" id="spiritMaturity" data={data} onChange={onChange} type="textarea" hint="Describe the difference. Where are you on that continuum? What practices and resources help you grow?" readOnly={ro} />
                <Field label="b) What challenged you spiritually in the past?" id="spiritPastChallenges" data={data} onChange={onChange} type="textarea" readOnly={ro} />
                <Field label="c) What are you challenged with spiritually now?" id="spiritCurrentChallenges" data={data} onChange={onChange} type="textarea" readOnly={ro} />
                <Field label="d) What is your 'go to' when spiritually challenged?" id="spiritGoTo" data={data} onChange={onChange} type="textarea" readOnly={ro} />
                <Field label="e) Beliefs, ideas, or images from childhood or earlier stages" id="spiritChildhood" data={data} onChange={onChange} type="textarea" readOnly={ro} />
                <Field label="f) How do these hold you back or challenge you today?" id="spiritHoldBack" data={data} onChange={onChange} type="textarea" hint="What are you doing to address these?" readOnly={ro} />
                <Field label="g) What does 'transformation' look like in your life?" id="spiritTransformation" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              </SectionCard>
            </>
          )}

          {section === "cocreating" && (
            <SectionCard title="Co-Creating a World That Works for All" subtitle="Reflecting on healing, justice, and belonging in the cultural context of your life and work." hue={170} sectionId="cocreating" comments={comments} onAddComment={onAddComment}>
              <Field label="Biases: assumptions or negative opinions toward other faiths, marginalized people, cultures" id="cocreateBiases" data={data} onChange={onChange} type="textarea" hint="Give specific examples of working with marginalized people or cultures. Name them. Be forthright about limitations. How might you overcome these?" readOnly={ro} />
              <Field label="Diversity, Equity, Inclusion, Justice & Belonging (100 words or less)" id="cocreateDEIJB" data={data} onChange={onChange} type="textarea" wordLimit="100" hint="What do DEIJB mean to you in your personal development work?" readOnly={ro} />
              <Field label="To what degree does this subject cause discomfort?" id="cocreateDiscomfort" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="2 examples of healing the discomfort" id="cocreateHealing" data={data} onChange={onChange} type="textarea" hint="What have you learned about yourself in responding?" readOnly={ro} />
              <Field label="What is your role in creating a world that works for all?" id="cocreateRole" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Summary Statement (100 words or less)" id="cocreateSummary" data={data} onChange={onChange} type="textarea" wordLimit="100" hint="Weave everything above into one cohesive statement of your personal spiritual development." readOnly={ro} />
            </SectionCard>
          )}

          {section === "relationships" && (
            <SectionCard title="Key Relationships" subtitle="Your Sponsor and Mentor are partners in your growth." hue={210} sectionId="relationships" comments={comments} onAddComment={onAddComment}>
              <Field label="Describe your relationship with your Sponsor. How can it be enhanced?" id="relSponsor" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Describe your relationship with your Mentor. How can it be enhanced?" id="relMentor" data={data} onChange={onChange} type="textarea" readOnly={ro} />
            </SectionCard>
          )}

          {section === "showcase" && (
            <ShowcaseSection data={data} onChange={onChange} role={role} userName={user?.name || ministerName || 'User'} />
          )}

          {section === "meetings" && reportId && user && (
            <MeetingsSection reportId={reportId} user={user} role={role} data={data} />
          )}

          {section === "growth" && reportId && (
            <GrowthDashboard reportId={reportId} reportYear={reportYear} />
          )}

          {section === "sponsor" && (
            <SectionCard title="Sponsor Report" subtitle="This section is to be completed and signed by your Sponsor." hue={35} sectionId="sponsor" comments={comments} onAddComment={onAddComment}>
              <div style={{ background: "rgba(255,200,100,0.15)", borderRadius: 12, padding: 16, marginBottom: 20, border: "1px solid rgba(200,160,60,0.2)" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#7a6030" }}>✦ After reviewing your Annual Report, your Sponsor completes this section.</p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                <Field label="Sponsor Name" id="spRepName" data={data} onChange={onChange} readOnly={ro} />
                <Field label="Sponsor's Community" id="spRepCommunity" data={data} onChange={onChange} readOnly={ro} />
              </div>
              <Field label="Describe your relationship with your sponsee" id="spRepRelationship" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="How could it be enhanced?" id="spRepEnhance" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="How is the licensed minister meeting their Job Description expectations?" id="spRepJobDesc" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="How is the minister progressing with Spiritual development?" id="spRepSpiritual" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Personal development plan implementation and steps for upcoming year" id="spRepDevPlan" data={data} onChange={onChange} type="textarea" hint="Include spiritual practices, prayer partners, etc." readOnly={ro} />
              <Field label="Any ethical issues you have supported with this minister?" id="spRepEthical" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Areas that could benefit from improvement" id="spRepImprovement" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Feedback communicated to your sponsee" id="spRepFeedback" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <div style={{ background: "rgba(255,255,255,0.5)", borderRadius: 12, padding: 20, marginTop: 20, border: "1px solid rgba(200,160,60,0.2)" }}>
                <p style={{ fontSize: 13, color: "#5a4a3a", margin: "0 0 12px", fontWeight: 600 }}>Terms of Acceptance</p>
                <p style={{ fontSize: 12, color: "#7a6a5a", lineHeight: 1.7, margin: "0 0 16px" }}>
                  By signing below, I confirm I have reviewed my sponsee's annual report, find all information fully accurate, and have met with my sponsee to review and provide feedback.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="Signature (typed name)" id="spRepSignature" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Date" id="spRepDate" data={data} onChange={onChange} type="date" readOnly={ro} />
                </div>
              </div>
            </SectionCard>
          )}

          {section === "mentor" && (
            <SectionCard title="Mentor Report" subtitle="This section is to be completed and signed by your Mentor." hue={35} sectionId="mentor" comments={comments} onAddComment={onAddComment}>
              <div style={{ background: "rgba(255,200,100,0.15)", borderRadius: 12, padding: 16, marginBottom: 20, border: "1px solid rgba(200,160,60,0.2)" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#7a6030" }}>✦ After reviewing your Annual Report, your Mentor completes this section.</p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                <Field label="Mentor Name" id="mnRepName" data={data} onChange={onChange} readOnly={ro} />
                <Field label="Mentor's Community" id="mnRepCommunity" data={data} onChange={onChange} readOnly={ro} />
              </div>
              <Field label="Describe your relationship with your mentee" id="mnRepRelationship" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="How could it be enhanced?" id="mnRepEnhance" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="How is the licensed minister using their Personal Development plan?" id="mnRepDevPlan" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="How is the licensed minister demonstrating a healthy ministry?" id="mnRepHealthy" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Areas that could benefit from improvement" id="mnRepImprovement" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <Field label="Feedback communicated to your mentee" id="mnRepFeedback" data={data} onChange={onChange} type="textarea" readOnly={ro} />
              <div style={{ background: "rgba(255,255,255,0.5)", borderRadius: 12, padding: 20, marginTop: 20, border: "1px solid rgba(200,160,60,0.2)" }}>
                <p style={{ fontSize: 13, color: "#5a4a3a", margin: "0 0 12px", fontWeight: 600 }}>Terms of Acceptance</p>
                <p style={{ fontSize: 12, color: "#7a6a5a", lineHeight: 1.7, margin: "0 0 16px" }}>
                  By signing below, I confirm I have reviewed my mentee's annual report, find all information fully accurate, and have met with my mentee to review and provide feedback.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  <Field label="Signature (typed name)" id="mnRepSignature" data={data} onChange={onChange} readOnly={ro} />
                  <Field label="Date" id="mnRepDate" data={data} onChange={onChange} type="date" readOnly={ro} />
                </div>
              </div>
            </SectionCard>
          )}

          {section === "export" && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{
                width: 100, height: 100, borderRadius: "50%", margin: "0 auto 24px",
                background: watercolorBg(220, "40%", "88%"),
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40,
              }}>📤</div>
              <h2 style={{ fontFamily: "'Georgia', serif", color: "#2d1b4e", margin: "0 0 12px" }}>Export Your Report</h2>
              <p style={{ color: "#7a6a8a", fontSize: 15, maxWidth: 500, margin: "0 auto 12px", lineHeight: 1.7 }}>
                Download a plain-text version that maps 1:1 to the original CMR format.
                You can paste this into the standard Word document for submission.
              </p>

              {/* Length Estimator */}
              {(() => {
                const plainText = generatePlainText(data, reportYear);
                const charCount = plainText.length;
                const estPages = Math.ceil(charCount / 3000); // 3000 chars per page

                const personalFields = [
                  'spiritDivine', 'spiritConnection', 'spiritPractices', 'spiritAlignment',
                  'spiritMaturity', 'spiritPastChallenges', 'spiritCurrentChallenges', 'spiritGoals',
                  'cocreateBiases', 'cocreateDEIJB', 'cocreateDiscomfort', 'cocreateAction'
                ];
                const personalChars = personalFields.reduce((acc, f) => acc + (data[f]?.length || 0), 0);
                const estPersonalPages = Math.ceil(personalChars / 3000);

                const isOverTotal = estPages > 20;
                const isOverPersonal = estPersonalPages > 10;

                return (
                  <div style={{ maxWidth: 600, margin: "0 auto 40px" }}>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                      <div style={{ 
                        flex: 1, padding: '16px', borderRadius: 16, background: isOverTotal ? '#fff0f0' : '#f0f9f4',
                        border: `1px solid ${isOverTotal ? '#ffcccc' : '#ccebdd'}`, textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 10, color: '#6a7a8a', textTransform: 'uppercase', marginBottom: 4 }}>Total Length</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: isOverTotal ? '#dc2626' : '#059669' }}>
                          ~{estPages} <span style={{ fontSize: 12, fontWeight: 500 }}>/ 20 pgs</span>
                        </div>
                      </div>
                      <div style={{
                        flex: 1, padding: '16px', borderRadius: 16, background: isOverPersonal ? '#fff0f0' : '#f0f9f4',
                        border: `1px solid ${isOverPersonal ? '#ffcccc' : '#ccebdd'}`, textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 10, color: '#6a7a8a', textTransform: 'uppercase', marginBottom: 4 }}>Personal Section</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: isOverPersonal ? '#dc2626' : '#059669' }}>
                          ~{estPersonalPages} <span style={{ fontSize: 12, fontWeight: 500 }}>/ 10 pgs</span>
                        </div>
                      </div>
                    </div>

                    {(isOverTotal || isOverPersonal) && (
                      <div style={{
                        background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12,
                        padding: '12px 16px', color: '#92400e', fontSize: 12, textAlign: 'left', marginBottom: 20
                      }}>
                        ⚠️ <strong>Warning:</strong> Your report may exceed length requirements. Consider condensing the
                        {isOverPersonal ? ' Personal/Spiritual' : ' overall'} sections.
                      </div>
                    )}
                  </div>
                );
              })()}

              <button onClick={exportPlain} style={{
                padding: "14px 40px", borderRadius: 30, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #7c4dba, #9b72cf)", color: "#fff",
                fontSize: 16, fontWeight: 600, boxShadow: "0 4px 15px rgba(124,77,186,0.3)",
                transition: "transform 0.2s",
              }} onMouseOver={e => (e.target as HTMLButtonElement).style.transform = "scale(1.03)"}
                 onMouseOut={e => (e.target as HTMLButtonElement).style.transform = "scale(1)"}>
                Download Plain Text Export
              </button>
              <p style={{ color: "#b0a0c0", fontSize: 12, marginTop: 20 }}>
                Tip: Send to your Sponsor and Mentor for their sections, then compile and email to FSMin@csl.org
              </p>
            </div>
          )}

          {/* Navigation buttons */}
          {section !== "welcome" && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32, paddingBottom: 20 }}>
              <button onClick={prev} style={{
                padding: "10px 24px", borderRadius: 20, border: "1.5px solid #d4c5e8",
                background: "rgba(255,255,255,0.6)", color: "#7c4dba", fontSize: 14,
                fontWeight: 500, cursor: "pointer",
              }}>← Previous</button>
              {curIdx < SECTIONS.length - 1 && (
                <button onClick={next} style={{
                  padding: "10px 24px", borderRadius: 20, border: "none",
                  background: "linear-gradient(135deg, #7c4dba, #9b72cf)", color: "#fff",
                  fontSize: 14, fontWeight: 500, cursor: "pointer",
                }}>Next →</button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
